import { useSuspenseQuery } from "@apollo/client/react";
import { type FormEvent, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedParams } from "react-router-typesafe-routes";

import { PageLoading } from "~/components/loading";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "~/components/ui";
import {
  DataImportProjectsModelProvider,
  useDataImportProjectsModel,
  useDipLifecycle,
} from "~/features/dataImportProjects/context";
import {
  GetDataImportProjectDetailDocument,
  type GetDataImportProjectDetailQuery,
  type GetDataImportProjectDetailQueryVariables,
  GetDataSourcesDocument,
  type GetDataSourcesQuery,
  type GetDataSourcesQueryVariables,
} from "~/features/dataImportProjects/gqls";
import {
  type SheetColumn,
  type SheetState,
  type SheetVariantKeys,
  useCategorySearch,
  useDataSourceDesigner,
  useSubmitDataSourceDesignerForm,
  VariantKeySlots,
} from "~/features/dataImportProjects/hooks";
import { DataImportProjectUtil } from "~/features/dataImportProjects/utils/dataImportProject";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";
import {
  AvailableCategoryList,
  SelectedCategoriesPanel,
  SheetPanel,
  UnconnectedCategoriesDialog,
} from "./DataSourceDesignerControls";

type DataSourceDesignerFormData = {
  name: string;
  references: Array<{ id: string; title: string }>;
  columns: SheetColumn[];
  sheets: SheetState[];
};

type DataSourceDesignerProps = {
  projectId: string;
  initialData: Partial<DataSourceDesignerFormData>;
};

function DataSourceDesigner({ projectId, initialData }: DataSourceDesignerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canExecuteMutation, isReadOnly } = useDipLifecycle();
  const { references } = useDataImportProjectsModel();
  const [name, setName] = useState(initialData.name ?? "");
  const [selectedReferences, setSelectedReferences] = useState(initialData.references ?? []);
  const [entitySearchTerm, setEntitySearchTerm] = useState("");
  const [relationSearchTerm, setRelationSearchTerm] = useState("");
  const { categories: entityCategories, loading: entityCategoriesLoading } = useCategorySearch(
    "entity",
    entitySearchTerm,
  );
  const { categories: relationCategories, loading: relationCategoriesLoading } = useCategorySearch(
    "relation",
    relationSearchTerm,
  );
  const {
    selectedCategories,
    sheets,
    activeSheetName,
    showUnconnectedAlert,
    unconnectedCategories,
    setActiveSheet,
    addCategory,
    removeCategory,
    updateRelationSourceTarget,
    updateRelationCardinality,
    setMainCategory,
    updateCategoryName,
    updateColumnLabel,
    updateColumnDescription,
    updateColumnSchemaType,
    toggleColumnRequired,
    moveColumn,
    removeColumn,
    duplicateColumn,
    updateColumnProperty,
    addCustomColumn,
    handleUpdatePlan,
    closeUnconnectedAlert,
  } = useDataSourceDesigner();
  const { handleDataSourceSubmit, isSubmitting } = useSubmitDataSourceDesignerForm({
    projectId,
    canExecute: canExecuteMutation,
  });

  const [variantKeys, setVariantKeys] = useState<SheetVariantKeys>({});
  const updateVariantKey = (sheetName: string, instanceId: string, variantKey: string) =>
    setVariantKeys((prev) => ({
      ...prev,
      [sheetName]: { ...prev[sheetName], [instanceId]: variantKey },
    }));
  const variantKeyErrors = useMemo(
    () => VariantKeySlots.validateVariantKeys(sheets, variantKeys),
    [sheets, variantKeys],
  );

  const referenceById = useMemo(
    () => new Map(selectedReferences.map((reference) => [reference.id, reference])),
    [selectedReferences],
  );
  const toggleReference = (reference: { id: string; title: string }) => {
    setSelectedReferences((prev) =>
      prev.some((item) => item.id === reference.id)
        ? prev.filter((item) => item.id !== reference.id)
        : [...prev, reference],
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canExecuteMutation() || !name.trim() || variantKeyErrors.length > 0) return;
    handleDataSourceSubmit({
      name: name.trim(),
      references: selectedReferences,
      columns: sheets.flatMap((sheet) => sheet.columns),
      sheets,
      variantKeys,
    });
  };

  return (
    <form onSubmit={submit} className="mx-auto grid max-w-[var(--content-max)] gap-6 p-8">
      <div className="sticky top-0 z-10 -mx-8 border-b border-border-default bg-bg-surface/95 px-8 py-4 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-dewey-title text-fg-1">{t("feature.dataSourceSchemaDesigner.name")}</h1>
            <p className="text-12-regular text-fg-3">{projectId}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(v2Route.dataImportProjects.detail.$buildPath({ params: { id: projectId } }))}
            >
              {t("ui.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                isReadOnly || isSubmitting || !name.trim() || sheets.length === 0 || variantKeyErrors.length > 0
              }
            >
              {isSubmitting ? t("ui.processing") : t("ui.save")}
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("model.dataSource.modelName")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[minmax(240px,0.8fr)_minmax(0,1.2fr)]">
          <div className="grid gap-2">
            <Label htmlFor="data-source-designer-name">{t("model.common.name")}</Label>
            <Input
              id="data-source-designer-name"
              value={name}
              required
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>{t("model.reference.modelNames")}</Label>
            <div className="max-h-[180px] overflow-auto rounded-[var(--radius-md)] border border-border-default">
              {references.length === 0 ? (
                <p className="p-3 text-12-regular text-fg-3">{t("feature.common.noData")}</p>
              ) : (
                <ul className="divide-y divide-border-default">
                  {references.map((reference) => (
                    <li key={reference.id} className="flex items-center gap-3 p-3">
                      <Checkbox
                        checked={referenceById.has(reference.id)}
                        aria-label={t("ui.select")}
                        onCheckedChange={() => toggleReference(reference)}
                      />
                      <span className="min-w-0 truncate text-12-regular text-fg-1">{reference.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
        <SelectedCategoriesPanel
          selectedCategories={selectedCategories}
          onRemove={removeCategory}
          onSetMain={setMainCategory}
          onRename={updateCategoryName}
          onUpdatePlan={handleUpdatePlan}
          onUpdateRelationSourceTarget={updateRelationSourceTarget}
          onUpdateRelationCardinality={updateRelationCardinality}
        />
        <Card>
          <CardHeader>
            <CardTitle>{t("feature.dataSourceSchemaDesigner.availableCategories")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="entity">
              <TabsList>
                <TabsTrigger value="entity">{t("model.record.entity.modelNames")}</TabsTrigger>
                <TabsTrigger value="relation">{t("model.record.relation.modelNames")}</TabsTrigger>
              </TabsList>
              <TabsContent value="entity" className="pt-4">
                <AvailableCategoryList
                  title={t("model.record.entity.modelNames")}
                  categories={entityCategories}
                  loading={entityCategoriesLoading}
                  onSearch={setEntitySearchTerm}
                  onAdd={addCategory}
                />
              </TabsContent>
              <TabsContent value="relation" className="pt-4">
                <AvailableCategoryList
                  title={t("model.record.relation.modelNames")}
                  categories={relationCategories}
                  loading={relationCategoriesLoading}
                  onSearch={setRelationSearchTerm}
                  onAdd={addCategory}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <SheetPanel
        sheets={sheets}
        activeSheetName={activeSheetName}
        selectedCategories={selectedCategories}
        variantKeys={variantKeys}
        variantKeyErrors={variantKeyErrors}
        onSheetChange={setActiveSheet}
        onUpdateVariantKey={updateVariantKey}
        onAddCustomColumn={addCustomColumn}
        onUpdateColumnLabel={updateColumnLabel}
        onUpdateColumnDescription={updateColumnDescription}
        onUpdateColumnSchemaType={updateColumnSchemaType}
        onToggleColumnRequired={toggleColumnRequired}
        onMoveColumn={moveColumn}
        onRemoveColumn={removeColumn}
        onDuplicateColumn={duplicateColumn}
        onUpdateColumnProperty={updateColumnProperty}
      />

      <UnconnectedCategoriesDialog
        open={showUnconnectedAlert}
        categories={unconnectedCategories}
        onClose={closeUnconnectedAlert}
      />
    </form>
  );
}

function DataSourceDesignerRouteContent() {
  const { id: projectId } = useTypedParams(v2Route.dataImportProjects.detail.dataSourceDesigner);
  const { tenantId } = useTenant();
  if (!projectId) {
    throw new Error("Project ID is required");
  }
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const { data: projectData } = useSuspenseQuery<
    GetDataImportProjectDetailQuery,
    GetDataImportProjectDetailQueryVariables
  >(GetDataImportProjectDetailDocument, { variables: { tenantId, where: { id: { _eq: projectId } } } });
  const project = projectData.dataImportProjects[0];
  if (!project) {
    throw new Error("Project not found");
  }

  const { data: dataSourcesData } = useSuspenseQuery<GetDataSourcesQuery, GetDataSourcesQueryVariables>(
    GetDataSourcesDocument,
    {
      variables: {
        tenantId,
        where: {
          dataSourceImportProjects: { dataImportProjectId: { _eq: projectId } },
        },
      },
    },
  );
  const dataSources = dataSourcesData.dataSources;
  const firstDataSource = dataSources[0];
  const initialData = {
    name: DataImportProjectUtil.dataSource.defaultName(project, dataSources.length),
    references:
      firstDataSource?.dataSourceReferences.map(({ reference }) => ({
        id: reference.id,
        title: reference.title,
      })) ?? [],
  };

  return <DataSourceDesigner projectId={projectId} initialData={initialData} />;
}

export function DataSourceDesignerPage() {
  return (
    <Suspense fallback={<PageLoading />}>
      <DataImportProjectsModelProvider>
        <DataSourceDesignerRouteContent />
      </DataImportProjectsModelProvider>
    </Suspense>
  );
}
